import React from 'react';
import { Button, Checkbox, Input, Loader, useToasts } from 'freemium-ui';
// import { History } from 'history';
// import { useHistory } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { get, uniq } from 'lodash';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import XLSX from 'xlsx';
import * as Yup from 'yup';
import {
  AccountInfoType,
  ContactListType,
  FormServFieldType,
} from '../../../../shared/types';
import { CustomFileType } from '../ImportContacts/types';
import Icon from '../../../../shared/components/Icon';
import { useUploadCsv } from '../../../../shared/hooks';
import {
  Center,
  FormActions,
  FormContainer,
  FormContent,
  FormField,
  FormHeader,
} from '../../../../shared/styles';
import {
  SaveContactListType,
  useGetContactLists,
  useSaveContactList,
} from '../../../screens/lists/hooks';
import { getJsonFromContacts, schema } from '../ImportContacts/utils';
import ContactsImporter from '../ImportContacts/ContactsImporter';

type NewListAndImportFormProps = {
  setIsDirty: (isDirty: boolean) => void;
  toggleDrawer: () => void;
  fields: FormServFieldType[];
  accountInfo?: AccountInfoType;
  contactsCount: number;
};

type FormValues = {
  fileMetaData: CustomFileType;
  importContacts: any;
  addToLists: string[];
} & Pick<ContactListType, 'label' | 'description' | 'display_id'>;

const NewListAndImportForm = (props: NewListAndImportFormProps) => {
  const { toggleDrawer, setIsDirty, fields, accountInfo, contactsCount } =
    props;
  const { t } = useTranslation();
  // const { push }: History = useHistory();
  const [, setToast] = useToasts();
  const [val, setVal] = React.useState(false);

  // const [savedContactListId, setSavedContactListId] =
  //   React.useState<string>('');
  const [loaderText, setLoaderText] = React.useState<string>('');
  const [loaderSubText, setLoaderSubText] = React.useState<string>('');
  const [takenNames, setTakenNames] = React.useState<string[]>([]);

  const form = useForm<FormValues>({
    resolver: (formValues: FormValues, ...resolverProps) => {
      return yupResolver(
        schema(fields, {
          label: Yup.string()
            .required(`${t('forms.err_msg.list_name_required')}`)
            .trim()
            .max(
              50,
              `${t('forms.err_msg.max_char', {
                len: '50',
              })}`,
            )
            .min(
              3,
              `${t('forms.err_msg.min_char', {
                len: '3',
              })}`,
            )
            .test({
              message: 'Error',
              name: 'name',
              test: function (name, context) {
                const { createError } = context || {};
                const currentTakenNames = !formValues.display_id
                  ? takenNames
                  : takenNames.filter((n) => n !== formValues.label);
                if (currentTakenNames.includes(name || '')) {
                  return createError({
                    message: `${t('forms.err_msg.contact_list_exists', {
                      list_name: `${name}`,
                    })}`,
                  });
                }
                return true;
              },
            }),
          description: Yup.string().nullable(true),
        }),
      )(formValues, ...resolverProps);
    },
    defaultValues: {
      label: '',
      description: '',
      fileMetaData: undefined,
      importContacts: {},
      addToLists: [],
    },
  });
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { isDirty, submitCount, errors },
  } = form;

  const onContactsImport = async (savedContactList: ContactListType) => {
    const { fileMetaData, importContacts }: FormValues = getValues();
    if (!fileMetaData || !fileMetaData.name) return;
    setLoaderText(`${t('uploading')}`);
    setLoaderSubText(`${t('upload_csv')}`);
    const jsonData = getJsonFromContacts(
      fields,
      savedContactList.display_id ? [savedContactList.display_id] : [],
      fileMetaData?.data || [],
      importContacts,
    );
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const file = new File([csv], fileMetaData.name);
    uploadCsv(file);
  };

  const {
    data: contactListsData,
    isLoading: isLoadingContactLists,
    isError: isContactListsError,
  } = useGetContactLists();

  const { mutate: saveContactList, isLoading: isSavingContactList } =
    useSaveContactList({
      setToast,
      callback: (
        isSuccess: boolean,
        response: any,
        variables: SaveContactListType,
      ) => {
        setLoaderText('');
        setLoaderSubText('');
        if (isSuccess) {
          // setSavedContactListId(savedContactList.display_id || '');
          const { fileMetaData }: FormValues = getValues();
          if (fileMetaData && fileMetaData.name) {
            onContactsImport(response);
          } else {
            toggleDrawer();
            // To-Do Enable after MCR latency issue is fixed
            // push(`/lists/${savedContactList.display_id || ''}`);
          }
        } else if (
          response?.response?.data === 'ContactList name is not unique'
        ) {
          setTakenNames((old) =>
            uniq([...old, variables.payload.label || '']).filter((v) => !!v),
          );
          form.setError('label', {
            message: `${t('forms.toast.error.contact_list_exists', {
              label: variables.payload.label,
            })}`,
          });
        }
      },
    });

  const { mutate: uploadCsv, isLoading: isUpploadingCsv } = useUploadCsv({
    callback: (isSuccess: boolean) => {
      setLoaderText('');
      setLoaderSubText('');
      if (isSuccess) {
        setToast({
          type: 'success',
          delay: 3000,
          text: `${t('forms.toast.success.email_after_importing')}`,
        });
        // To-Do Enable after MCR latency issue is fixed
        // push(`/lists/${savedContactListId}`);
        toggleDrawer();
      }
    },
  });

  const onSubmit = (data: ContactListType) => {
    setLoaderText(`${t('creating')}`);
    setLoaderSubText(`${t('create_list')}`);
    saveContactList({ payload: data });
  };

  const fileMetaData: CustomFileType = form.watch('fileMetaData') || {};

  React.useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty]);

  React.useEffect(() => {
    const newNames = (contactListsData?.contactList || [])
      .map((contactList) => contactList.label || '')
      .filter((v) => !!v);
    setTakenNames((old) => uniq([...old, ...newNames]));
  }, [contactListsData]);

  if (isLoadingContactLists || isSavingContactList || isUpploadingCsv)
    return (
      <Center>
        <Loader />
        {loaderText && <div className="text">{loaderText}</div>}
        {loaderSubText && <div className="sub-text">{loaderSubText}</div>}
      </Center>
    );

  if (isContactListsError)
    return <Center>{t('empty_state.message.network_error')}</Center>;

  return (
    <FormContainer
      onSubmit={handleSubmit(onSubmit, (err) => console.log('ERR', err))}
    >
      <FormHeader hideBorder>
        <div className="heading">{t('lists.list_fields.list_name')}</div>
      </FormHeader>
      <FormContent>
        <FormField>
          <Input
            {...register('label')}
            id="label"
            label={`${t('forms.labels.contact_list_name')}`}
            isRequired
            isErrored={submitCount > 0 && Boolean(errors?.label?.message)}
            errorText={submitCount > 0 ? `${errors?.label?.message || ''}` : ''}
          />
        </FormField>
        <FormField>
          <div className="heading">{t('contacts.import_contact.heading')}</div>
          <ContactsImporter
            component="new-list"
            contactLists={contactListsData?.contactList || []}
            hideListAdd
            isLoadingContactLists={isLoadingContactLists}
            fields={fields}
            form={form}
            accountInfo={accountInfo}
            contactsCount={contactsCount}
          />
        </FormField>
      </FormContent>
      {!get(watch('fileMetaData'), ['lastModified'], false) && (
        <CustomDisplayBox>
          <div className="head-info">
            <Icon width="16" height="16" glyph="info" />
            <div>{t('contacts.new_list_and_import.how_to_add')}</div>
          </div>
          <div className="desc-info">
            <Trans
              i18nKey="contacts.new_list_and_import.desc"
              components={{
                bold: <b />,
              }}
            />
          </div>
        </CustomDisplayBox>
      )}
      {fileMetaData.name ? (
        <div className="disclaimer">
          <Checkbox
            checked={val}
            isRequired={true}
            name="disclaimer"
            onChange={() => {
              setVal(!val);
            }}
          >
            <span className="disclaimer-text">
              {t('contacts.import_contact.disclaimer_text')}
            </span>
          </Checkbox>
        </div>
      ) : null}

      <FormActions>
        <div />
        <div>
          <Button
            inline
            htmlType="reset"
            type="secondary"
            onClick={toggleDrawer}
          >
            {t('cancel')}
          </Button>
          <Button
            inline
            htmlType="submit"
            disabled={
              !isDirty || isSavingContactList || (!!fileMetaData.name && !val)
            }
          >
            {t('save')}
          </Button>
        </div>
      </FormActions>
    </FormContainer>
  );
};

export default NewListAndImportForm;

const CustomDisplayBox = styled.div`
  margin: 32px;
  padding: 22px;
  background: #f5f7f9;
  border-radius: 8px;
  div.head-info {
    & > :not(:last-child) {
      margin-right: 16px;
    }
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: #242f38;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
  }
  div.desc-info {
    font-weight: 400;
    font-size: 12px;
    line-height: 23px;
    color: #475867;
    strong {
      font-weight: bold;
    }
  }
`;
